import React, { useCallback, FC, useMemo, useState, useEffect } from 'react';
import { useNavVirtualSportsTopicStoreCreator } from '../components/virtual-sports/virtual-sports-nav.hooks';
import { 
    UseSportsNavStore, 
    Spinner,
    SportsNavStoreData,
    SportsNavModel,
    LayoutType,
    SportsNavType,
    VirtualSportRouteParams,
    RouteParser,
    navBem,
    useRpcState,
} from '@om-fe/widgets';
import { useReady } from '@om-fe/store';
import RedirectTo from '@om-fe/core/components/redirect-to/redirect-to';
import { RouteNames } from '@om-fe/core/core/routing/utils';
import { useParams } from 'react-router';
import { removeAccents } from '@om-fe/core/core/routing/provider';
import { DisciplineLiveStatus, DisciplinesType, VirtualSportsDisciplines } from '@om-fe/models';
import UserBehaviorTrackingContext from '@om-fe/core/components/user-behavior-tracking-context/user-behavior-tracking-context';
import { SportsNav } from '@om-fe/widgets/dist/widgets/navs/sports-nav/sports-nav';
import { UserBehaviorTrackingLocation } from '@om-fe/core/components/user-behavior-tracking-context/user-behavior-tracking.utils';
import { AppState } from '@om-fe/core/core/types';
import { PlatformTypes } from '@om-fe/utils';
import { connect } from 'react-redux';
import { useEMRouting } from '@om-fe/core/core/routing/with-routing';
import { createWidgetsVirtualRouteParser } from '@om-fe/core/core/routing/widgets/widgets-routes';
import CategoryNav, { ALL_CATEGORIES_ID } from '@om-fe/core/components/nav-secondary/category-nav/category-nav';
import { CategoryNavItemRouteParamsMap } from '@om-fe/core/components/nav-secondary/category-nav/category-nav-item';
import { categoryNavSlice, CategoryNavStateModel } from '@om-fe/core/components/nav-secondary/category-nav/category-nav-store';
import getSliceName from '@om-fe/core/utils/get-slice-name';
import getSlice from '@om-fe/core/utils/get-slice';
import { TournamentMatchListVirtualSport } from '../components/virtual-sports/tournament-match-list-virtual-sport';
import getConfig, { OMFE2ConfigModel } from '@om-fe/core/utils/config';
import { getMainMarketsLimit } from '@om-fe/core/components/match-list/utils/match-list.utils';
import { GetMarketGroupsOptions, GetMarketGroupsResponse } from '@om-fe/api';
import MarketTemplateMappingsContext from '@om-fe/core/components/market-template-mappings-context/marketTemplateMappingsContext';
import { WithMarketTemplateMappingsProps } from '@om-fe/core/components/market-template-mappings-context/with-market-template-mappings';
import { TournamentMatchListStoreModel } from '../components/match-list/containers/tournament-match-list/tournament-match-list.constants';
import { getTournamentMatchListSliceName } from '../components/match-list/containers/tournament-match-list/tournament-match-list.utils';
import { VirtualSportsStreaming } from '@om-fe/core/components/virtual-sports-streaming/virtual-sports-streaming';
import PageNotFound from '@om-fe/core/pages/theme1/404';
import { VirtualHorsesLazy } from '@om-fe/core/core/widgets/retail-widgets/loaders/retail-lazy-loaders';
import { useI18NContext } from '@om-fe/core/core/i18n/useI18ncontext';
import Metadata from '@om-fe/core/components/metadata/metadata';
import { Breadcrumbs } from '../components/breadcrumbs/breadcrumbs';
import { DisciplineRouteType } from '@om-fe/core/components/discipline-anchor/discipline-route.utils';

interface VirtualSportsRouteParams {
    readonly virtualSportsName: string;
    readonly virtualSportsId: string;
    readonly virtualTournamentId: string;
    readonly virtualTournamentName: string;
}

interface VirtualSportStoreProps {
    readonly deviceType?: PlatformTypes;
    readonly tournamentIdStore?: Record<string, string | undefined>;
    readonly tournamentNameStore?: string;
    readonly mainMarketsLimit: number;
    readonly streamMappingId?: string;
    readonly streamMappingTournamentId?: string;
    readonly isLarge?: boolean;
}

type VirtualSportProps = WithMarketTemplateMappingsProps;

const categoryNavInstanceId: string = `CategoryNav-LiveUpcomingTabs-${DisciplineLiveStatus.BOTH}`;

const config: OMFE2ConfigModel = getConfig();
const marketSelector: boolean = config?.matchListConfig?.matchListSelector?.enabled ?? false;

export const VirtualSportPage = ({
    deviceType,
    tournamentIdStore,
    tournamentNameStore,
    mainMarketsLimit,
    marketTemplateMappings,
    streamMappingId,
    streamMappingTournamentId,
    isLarge,
}: VirtualSportProps & VirtualSportStoreProps) => {
    const { gettext } = useI18NContext();
    const metadataText: string = gettext('Virtual Sports');
    const { virtualSportsId, virtualSportsName, virtualTournamentId } = useParams<VirtualSportsRouteParams>();
    const [prevVirtualTournamentId, setPrevVirtualTournamentId] = useState<string | undefined>(virtualTournamentId);
    const useNavVirtualSportsStore: UseSportsNavStore = useNavVirtualSportsTopicStoreCreator();
    const ready: boolean = useReady(useNavVirtualSportsStore);
    const { getFullPath } = useEMRouting();
    const params: GetMarketGroupsOptions = useMemo(() => ({ sportId: virtualSportsId!, liveStatus: DisciplineLiveStatus.BOTH }), [virtualSportsId]);
    const [rpcResponse] = useRpcState<GetMarketGroupsResponse>('marketGroupsOverview', params);
    const [extraParam, setExtraParam] = useState<string | undefined>(undefined);
    const [streamId, setStreamId] = useState<string | undefined>(undefined);
    const [streamTournamentId, setStreamTournamentId] = useState<string | undefined>(undefined);
    const baseRouteParser: ((params: VirtualSportRouteParams) => string) = useMemo(() => {
        return createWidgetsVirtualRouteParser(getFullPath);
    }, [getFullPath]);
    const virtualHorseEnabled: boolean = !!config?.virtualHorseWidget?.offerUrl && !!config?.virtualHorseWidget?.offerHorseUrl && !!config?.virtualHorseWidget?.videoUrl;

    const routeParser: RouteParser<VirtualSportRouteParams> = useCallback(({
        virtualSportsId, 
        virtualSportsName, 
        virtualTournamentId,
        virtualTournamentName,
    }: VirtualSportRouteParams) => {
        return baseRouteParser({
            virtualSportsId, 
            virtualSportsName: removeAccents(virtualSportsName) as string,
            virtualTournamentId,
            virtualTournamentName: removeAccents(virtualTournamentName),
        });
    }, [baseRouteParser]);

    const [redirectItem, equalData]: [SportsNavModel | undefined, boolean] = useNavVirtualSportsStore(useCallback((
        state: SportsNavStoreData,
    ): [SportsNavModel | undefined, boolean] => {
        const sportMergeList: string[] = [...state.liveList, ...state.notLiveList];
        const sportBothList: string[] = state.bothList;
        const sportItemsList: Record<string, SportsNavModel> = state.items;
        const firstIndex = sportBothList.findIndex(sport => sportMergeList.includes(sport));

        return [
            !virtualSportsId || (virtualSportsId && ready && (!sportMergeList.includes(virtualSportsId)))
            ? (state.items[sportBothList[firstIndex]] || state.items[sportBothList[0]])
            : undefined, 
            (ready && (Object.keys(sportBothList).length !== Object.keys(sportItemsList).length) && !sportItemsList[virtualSportsId]) ? true : false,
        ];
    }, [virtualSportsId, ready]));

    useEffect(() => {
        setStreamId(undefined);
        setStreamTournamentId(undefined);
        setExtraParam(undefined);
        setPrevVirtualTournamentId((prev) => {
            if (prev === virtualTournamentId) return prev;

            return virtualTournamentId;
        });
    }, [virtualTournamentId]);

    useEffect(() => {
        if (!streamId && streamMappingId && streamMappingTournamentId === virtualTournamentId) {
            setStreamId(streamMappingId);
            setStreamTournamentId(virtualTournamentId);
        }
    }, [streamId, virtualTournamentId, streamMappingId, streamMappingTournamentId]);

    const streamCallback = useCallback((extraParam: string) => {
        setExtraParam((prev) => prev === extraParam ? prev : extraParam);
    },[]);

    if (!ready) {
        return (
            <Spinner />
        );
    }

    if (redirectItem && tournamentIdStore && tournamentIdStore[virtualSportsId]) {
        return (
            <RedirectTo
                routeName={RouteNames.newVirtualSports}
                routeParams={{
                    virtualSportsId: redirectItem.id,
                    virtualSportsName: removeAccents(redirectItem.name),
                    virtualTournamentId: virtualSportsId === redirectItem.id ? tournamentIdStore[virtualSportsId] : undefined,
                    virtualTournamentName: virtualSportsId === redirectItem.id ? removeAccents(tournamentNameStore) : undefined,
                }}
            />
        );
    }

    if (virtualSportsId && equalData) {
        return (
            <RedirectTo routeName={RouteNames.newVirtualSports} />
        );
    }

    if ((!redirectItem && tournamentIdStore && tournamentIdStore[virtualSportsId] && !virtualTournamentId && tournamentIdStore[virtualSportsId] !== virtualTournamentId)) {
        return (
            <RedirectTo
                routeName={RouteNames.newVirtualSports}
                routeParams={{
                    virtualSportsId,
                    virtualSportsName,
                    virtualTournamentId: tournamentIdStore[virtualSportsId],
                    virtualTournamentName: removeAccents(tournamentNameStore),
                }}
            />
        );
    }

    if (!redirectItem && !virtualSportsId && !virtualTournamentId && tournamentIdStore && !tournamentIdStore[virtualSportsId]) {
        return (
            <PageNotFound />
        );
    }

    const routeParamsMap: CategoryNavItemRouteParamsMap = {
        eventCategoryName: 'virtualTournamentName',
        eventCategoryId: 'virtualTournamentId',
        disciplineId: 'virtualSportsId',
        disciplineName: 'virtualSportsName',
        activeTab: '',
    };

    return (
        <>
            <Metadata
                title={metadataText}
                keywords={metadataText}
                description={metadataText}
            />
            <Breadcrumbs
                disciplineId={DisciplinesType.VirtualSports}
                isLarge={isLarge}
                routeType={DisciplineRouteType.Location}
            />
            <div className={'NavWrapper'}>
                <SportsNav
                    activeItemId={virtualSportsId}
                    virtualSportRoute={routeParser}
                    useStore={useNavVirtualSportsStore}
                    className={`${SportsNavType.Virtual}SportsNav`}
                    showIcon
                    hasArrows={deviceType === 'desktop'}
                    layoutType={LayoutType.Main}
                    isNewVirtual={true}
                />
            </div>
            {virtualSportsId === VirtualSportsDisciplines.VirtualHorseRacing ? (
                virtualHorseEnabled ? <VirtualHorsesLazy /> : <PageNotFound />
            ) : (
                <>
                <div className={navBem({
                    modifier: {
                        [`SportId-${virtualSportsId}`]: true,
                        CategoryNav: true,
                    }
                })}>
                    <CategoryNav
                        disciplineId={redirectItem?.id || virtualSportsId}
                        disciplineName={removeAccents(redirectItem?.name) || virtualSportsName}
                        instanceId={categoryNavInstanceId}
                        routeName={RouteNames.newVirtualSports}
                        routeParamsMap={routeParamsMap}
                        genericId={virtualTournamentId || ALL_CATEGORIES_ID}
                        liveStatus={DisciplineLiveStatus.NOT_LIVE}
                        tournamentMode={true}
                        hideAll={true}
                        flagLocation={true}
                    />
                </div>
                {virtualTournamentId !== prevVirtualTournamentId ?
                    <Spinner />
                :
                !!(ready && virtualSportsId && marketSelector && virtualTournamentId) && (
                    <>
                        <MarketTemplateMappingsContext.Provider value={marketTemplateMappings}>
                            <UserBehaviorTrackingContext.Provider value={{ userBehaviorTrackingPage: RouteNames.newVirtualSports, userBehaviorTrackingLocation: UserBehaviorTrackingLocation.VirtualMatchList }}>
                                {streamId && streamTournamentId === virtualTournamentId && (
                                    <VirtualSportsStreaming
                                        key={`${virtualTournamentId}-${streamId}`}
                                        disciplineId={virtualSportsId}
                                        eventId={streamId}
                                        streamCallback={streamCallback}
                                    />
                                )}
                                <TournamentMatchListVirtualSport
                                    virtualSportsId={virtualSportsId}
                                    hasMarketSelector={marketSelector}
                                    mainMarketsLimit={mainMarketsLimit}
                                    virtualTournamentId={virtualTournamentId}
                                    deviceType={deviceType}
                                    marketGroupData={rpcResponse}
                                    extraParam={streamTournamentId === virtualTournamentId ? extraParam : undefined}
                                />
                            </UserBehaviorTrackingContext.Provider>
                        </MarketTemplateMappingsContext.Provider>
                    </>
                )}
                </>
            )}
        </>
    );
}

export const VirtualSport: FC<VirtualSportProps & VirtualSportStoreProps> = connect(
    (state: AppState): VirtualSportStoreProps => {
        const sliceName: string = getSliceName(categoryNavSlice, categoryNavInstanceId);
        const slice: CategoryNavStateModel | undefined = getSlice<CategoryNavStateModel>(state, sliceName);
        const objectSliceList: string[] | undefined = slice ? [...slice?.parts?.live?.list, ...slice?.parts?.notLive?.list] : undefined;
        const sportId: string = state?.routing?.match?.params?.virtualSportsId as string;
        const activeVirtualTournamentId: string = state.routing.match?.params.virtualTournamentId || '';
        const streamSliceKey: string = getTournamentMatchListSliceName(`VirtualTournament-MatchList-${activeVirtualTournamentId}`);
        const sliceData: TournamentMatchListStoreModel | undefined = state[streamSliceKey];

        return {
            deviceType: state.deviceInfo?.platform.type,
            tournamentIdStore: {
                [sportId]: objectSliceList?.length ? objectSliceList[0] : undefined,
            },
            tournamentNameStore: objectSliceList?.length ? slice?.parts?.notLive?.items[objectSliceList[0]]?.name : undefined,
            mainMarketsLimit: getMainMarketsLimit(state.breakpoints),
            streamMappingId: sliceData?.filteredMatchesIds?.length ? sliceData?.filteredMatchesIds[0] : undefined,
            streamMappingTournamentId: sliceData?.tournamentId,
            isLarge: (state.breakpoints.large || state.breakpoints.xl),
        };
    },
)(VirtualSportPage);